import React from 'react';
import { BaseModal } from 'components/UI';
import { Formik, Field } from 'formik';
import * as Yup from 'yup';
import i18n from 'i18n-js';
import InputAdornment from '@material-ui/core/InputAdornment';

import { Box, Grid, makeStyles, Typography } from '@material-ui/core';
import { TextField, TextareaField } from 'components/FormFields';
import { VehiclesAssetClassesSelect, VehiclesTypesSelect } from 'components/AsyncFields';
import { ASSET_CLASS } from '../../../constants/vehicleTypes';

function EditVehicleDetailsModal({
  canEdit,
  open = true,
  vehicle = {},
  onCancel,
  onSubmit,
  errors,
}) {
  const formRef = React.useRef(null);
  const useStyles = makeStyles((theme) => ({
    optional: {
      fontSize: 12,
      color: theme.palette.secondary.light,
    },
  }));
  const classes = useStyles();
  const formSchema = Yup.object().shape({
    name: Yup.string().required(i18n.t('common.required_field')),
    vehicle_type_id: Yup.string().required(i18n.t('common.required_field')),
    color: Yup.string(),
    price: Yup.number().when('asset_class', {
      is: (val) => val === 'BUS',
      then: Yup.number().required(),
    }),
    seats: Yup.number().when('asset_class', {
      is: (val) => val === 'BUS',
      then: Yup.number().min(1),
    }),
    license_plate: Yup.string(),
    asset_class: Yup.string().required(i18n.t('common.required_field')),
    details: Yup.string(),
    vin: Yup.string().when('asset_class', {
      is: (val) => ASSET_CLASS.includes(val),
      then: Yup.string().length(17),
    }),
  });

  const initialValues = {
    vin: vehicle?.vin || '',
    name: vehicle?.name || '',
    color: vehicle?.color || '',
    seats: vehicle?.seats || '',
    details: vehicle?.details || '',
    license_plate: vehicle?.license_plate || '',
    vehicle_type_id: vehicle?.vehicle_type?.id || '',
    asset_class: vehicle?.vehicle_type?.asset_class || '',
    price: vehicle?.price ? parseFloat(vehicle?.price / 100).toFixed(2) : '',
    vehicle_group_id: vehicle?.vehicle_group?.parent?.id || vehicle?.vehicle_group?.id || '',
  };

  let params = {};

  if (vehicle?.vehicle_type?.id) {
    params.id = vehicle?.vehicle_type?.id;
  }

  return (
    <BaseModal
      open={open}
      hasEnterButton
      hasCancelButton
      onEnter={() => {
        formRef.current.submitForm();
      }}
      onCancel={onCancel}
      enableCloseButton={false}
      enterButtonLabel={i18n.t('common.update')}
      aria-labelledby="modal-title"
      title={i18n.t('vehicle.update_vehicle_information')}
      size="lg"
    >
      <Box mt={-1}>
        <Formik
          innerRef={formRef}
          initialValues={initialValues}
          validationSchema={formSchema}
          enableReinitialize={true}
          onSubmit={onSubmit}
        >
          {({ handleSubmit, values }) => (
            <form noValidate onSubmit={handleSubmit}>
              <Grid
                container
                spacing={2}
                direction="row"
                justifyContent="flex-start"
                className={!canEdit ? 'disabled' : ''}
                alignItems="flex-start"
              >
                <Grid item xs={12} md={12}>
                  <Typography variant="h6">
                    <Box fontWeight="500" fontSize={14} mb={-4}>
                      {i18n.t('vehicle.vehicle_general_information')}
                    </Box>
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={6}>
                  <Field
                    type="text"
                    name="name"
                    disabled={!canEdit}
                    component={TextField}
                    asyncErrors={errors?.name}
                    label={i18n.t('common.name')}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={6}>
                  <Field
                    name="asset_class"
                    disabled={!canEdit}
                    component={VehiclesAssetClassesSelect}
                    customparams={{ asset_class: values.asset_class }}
                    label={i18n.t('vehicle_types.asset_class')}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={6}>
                  <Field
                    name="vehicle_type_id"
                    component={VehiclesTypesSelect}
                    disabled={!canEdit || !values?.asset_class}
                    customParams={{ asset_class: values.asset_class, ...params }}
                    label={i18n.t('vehicle_types.vehicle_type')}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="h6">
                    <Box fontWeight="500" fontSize={14} mt={1} mb={-3}>
                      {i18n.t('vehicle.vehicle_details')}
                    </Box>
                  </Typography>
                </Grid>
                {ASSET_CLASS.includes(values.asset_class) && (
                  <>
                    <Grid item xs={12} sm={6} md={6}>
                      <Field
                        type="text"
                        required={false}
                        name="license_plate"
                        disabled={!canEdit}
                        component={TextField}
                        asyncErrors={errors?.license_plate}
                        label={i18n.t('vehicle.license_plate')}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={6}>
                      <Field
                        type="text"
                        name="vin"
                        required={false}
                        disabled={!canEdit}
                        component={TextField}
                        asyncErrors={errors?.vin}
                        label={i18n.t('vehicle.vin')}
                      />
                    </Grid>
                  </>
                )}
                <Grid item xs={12} sm={6} md={6}>
                  <Field
                    step="any"
                    type="number"
                    name="price"
                    label={i18n.t('vehicle.price')}
                    format="price"
                    required={values.asset_class === 'BUS'}
                    component={TextField}
                    disabled={!canEdit}
                    asyncErrors={errors?.price}
                    disabledText={!canEdit}
                    startAdornment={
                      <InputAdornment position="start" style={{ marginRight: -8 }}>
                        $
                      </InputAdornment>
                    }
                  />

                  <Typography variant="h6">
                    <Box className={classes.optional}>
                      {i18n.t(
                        values.asset_class === 'BUS' ? 'common.required_field' : 'common.optional'
                      )}
                    </Box>
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={6}>
                  <Field
                    type="text"
                    name="color"
                    required={false}
                    disabled={!canEdit}
                    component={TextField}
                    asyncErrors={errors?.color}
                    label={i18n.t('vehicle.color')}
                  />
                  <Typography variant="h6">
                    <Box className={classes.optional}>{i18n.t('common.optional')}</Box>
                  </Typography>
                </Grid>
                {values.asset_class === 'BUS' && (
                  <Grid item xs={12} sm={6} md={6}>
                    <Field
                      type="number"
                      name="seats"
                      disabled={!canEdit}
                      component={TextField}
                      asyncErrors={errors?.seats}
                      label={i18n.t('vehicle.number_of_seats')}
                    />
                  </Grid>
                )}
                <Grid item xs={12} sm={12} md={12}>
                  <Field
                    rows={2}
                    type="text"
                    name="details"
                    required={false}
                    variant="standard"
                    label={i18n.t('common.description')}
                    disabled={!canEdit}
                    component={TextareaField}
                    asyncErrors={errors?.details}
                  />
                </Grid>
              </Grid>
            </form>
          )}
        </Formik>
      </Box>
    </BaseModal>
  );
}

export default EditVehicleDetailsModal;
